import store from '@/store';
import i18n from '@/configured/vue-i18n';
import log from '@/configured/logger';

const inFlightLoads = new Map();
const bootstrapWindows = new Map();
const completedBootstrapWindows = new Set();

function getCurrentLanguage() {
  return store.state[`${i18n.store.namespace}`].language || 'en';
}

export function normalizeBootstrapOptions(
  payload,
  defaults = {
    forceInline: false,
    mode: 'startup',
    bootstrapContext: null,
  }
) {
  if (typeof payload === 'boolean') {
    return {
      ...defaults,
      forceInline: payload,
    };
  }

  if (!payload || typeof payload !== 'object') {
    return { ...defaults };
  }

  return {
    ...defaults,
    ...payload,
    forceInline:
      'forceInline' in payload
        ? !!payload.forceInline
        : defaults.forceInline,
  };
}

export function createBootstrapContext({
  language = getCurrentLanguage(),
  mode = 'startup',
} = {}) {
  return {
    language,
    mode,
    key: `${language}:${mode}`,
  };
}

export function hasCompletedBootstrapWindow(context) {
  return completedBootstrapWindows.has(context.key);
}

export function isBootstrapRequest(context) {
  return !!context;
}

function ensureBootstrapWindow(context) {
  if (!bootstrapWindows.has(context.key)) {
    bootstrapWindows.set(context.key, {
      key: context.key,
      language: context.language,
      mode: context.mode,
      requestCount: 0,
      startedCount: 0,
      duplicateCount: 0,
      requestKeys: {},
      duplicateKeys: {},
      logged: false,
      startedAt: Date.now(),
    });
  }

  return bootstrapWindows.get(context.key);
}

function recordBootstrapRequest(context, metricKey, reused) {
  if (!context) {
    return;
  }

  const metrics = ensureBootstrapWindow(context);
  metrics.requestCount += 1;
  metrics.requestKeys[metricKey] = (metrics.requestKeys[metricKey] || 0) + 1;

  if (reused) {
    metrics.duplicateCount += 1;
    metrics.duplicateKeys[metricKey] =
      (metrics.duplicateKeys[metricKey] || 0) + 1;
  } else {
    metrics.startedCount += 1;
  }
}

export function hasBootstrapRequested(context, metricKey) {
  if (!context) {
    return false;
  }

  const metrics = bootstrapWindows.get(context.key);

  if (!metrics) {
    return false;
  }

  return !!metrics.requestKeys[metricKey];
}

export function runSharedLoad(
  loadKey,
  loader,
  { bootstrapContext = null, metricKey = loadKey } = {}
) {
  const existingLoad = inFlightLoads.get(loadKey);

  if (existingLoad) {
    recordBootstrapRequest(bootstrapContext, metricKey, true);
    return existingLoad;
  }

  recordBootstrapRequest(bootstrapContext, metricKey, false);

  const promise = Promise.resolve()
    .then(loader)
    .finally(() => {
      inFlightLoads.delete(loadKey);
    });

  inFlightLoads.set(loadKey, promise);
  return promise;
}

export function runBootstrapLoad(context, loader) {
  return runBootstrapLoadWithOptions(context, loader);
}

export function runBootstrapLoadWithOptions(
  context,
  loader,
  { forceReload = false } = {}
) {
  const loadKey = `base-data:${context.key}`;
  const existingLoad = inFlightLoads.get(loadKey);

  if (!existingLoad && forceReload) {
    completedBootstrapWindows.delete(context.key);
    bootstrapWindows.delete(context.key);
  }

  if (!forceReload && hasCompletedBootstrapWindow(context)) {
    return Promise.resolve();
  }

  return runSharedLoad(
    loadKey,
    async () => {
      try {
        const result = await loader();
        completedBootstrapWindows.add(context.key);
        return result;
      } finally {
        logBootstrapSummary(context);
      }
    },
    { bootstrapContext: context, metricKey: 'base-data' }
  );
}

export function logBootstrapSummary(context) {
  const metrics = bootstrapWindows.get(context.key);

  if (!metrics || metrics.logged) {
    return;
  }

  metrics.logged = true;

  log.info('Bootstrap summary', {
    language: metrics.language,
    mode: metrics.mode,
    requestCount: metrics.requestCount,
    startedCount: metrics.startedCount,
    duplicateCount: metrics.duplicateCount,
    duplicateKeys: metrics.duplicateKeys,
    durationMs: Date.now() - metrics.startedAt,
  });
}
